/**
 * SearchConfigFormatter - 搜索配置格式化器工厂类
 *
 * 提供基于item层面的可扩展格式化机制，替代硬编码的配置处理逻辑
 *
 * @author Claude
 * @created 2025-09-16
 * @version 1.0.0
 *
 * 支持配置格式：
 * 1. 传统字符串：value: "getDateprocessing('month_first')"
 * 2. 对象格式：formatter: { type: "date_processor", method: "getDateprocessing", params: ["month_first"] }
 * 3. 字符串简写：formatter: "date:getDateprocessing:month_first"
 */

import { getDateprocessing } from '@/utils/index'

/**
 * 日期格式化器 - 处理各种日期相关的格式化需求
 */
class DateFormatter {
  constructor() {
    this.type = 'date'
  }

  /**
   * 格式化单个日期值
   * @param {Object} item - 配置项
   * @param {string} value - 原始值
   * @returns {string|null} 格式化后的值
   */
  format(item, value) {
    if (!value || typeof value !== 'string') {
      return null
    }

    try {
      // 处理getDateprocessing()直接调用
      if (value.includes('getDateprocessing(')) {
        return this.processGetDateprocessingCall(value)
      }

      // 处理模板字符串：${getDateprocessing(0)} 23:59:59
      if (value.includes('${getDateprocessing(')) {
        return this.processTemplateString(value)
      }

      return null
    } catch (error) {
      console.warn('[DateFormatter] 日期格式化失败:', item.key, value, error)
      return null
    }
  }

  /**
   * 处理getDateprocessing()函数调用
   */
  processGetDateprocessingCall(value) {
    const match = value.match(/getDateprocessing\(([^)]+)\)/)
    if (match) {
      const param = match[1]
      // ES2017兼容：处理字符串参数和数字参数
      const paramValue = param.startsWith("'") && param.endsWith("'")
        ? param.slice(1, -1)
        : parseInt(param)
      return getDateprocessing(paramValue)
    }
    return null
  }

  /**
   * 处理模板字符串
   */
  processTemplateString(value) {
    return value.replace(/\$\{getDateprocessing\(([^)]+)\)\}/g, function(match, param) {
      // ES2017兼容：使用function替代箭头函数
      const paramValue = param.startsWith("'") && param.endsWith("'")
        ? param.slice(1, -1)
        : parseInt(param)
      return getDateprocessing(paramValue)
    })
  }

  /**
   * 基于新配置格式的处理
   * @param {Object} formatterConfig - 格式化器配置
   * @returns {string|null}
   */
  formatByConfig(formatterConfig) {
    if (!formatterConfig || formatterConfig.type !== 'date_processor') {
      return null
    }

    try {
      const method = formatterConfig.method
      const params = formatterConfig.params || []

      if (method === 'getDateprocessing') {
        const param = params[0]
        return getDateprocessing(param)
      }

      return null
    } catch (error) {
      console.warn('[DateFormatter] 配置格式化失败:', formatterConfig, error)
      return null
    }
  }
}

/**
 * 模板格式化器 - 处理复杂的模板字符串
 */
class TemplateFormatter {
  constructor() {
    this.type = 'template'
  }

  format(item, value) {
    if (!value || typeof value !== 'string' || !value.includes('${')) {
      return null
    }

    try {
      // 处理复杂模板字符串
      return value.replace(/\$\{([^}]+)\}/g, function(match, expr) {
        try {
          // 安全的表达式解析
          if (expr.includes('getDateprocessing(')) {
            const funcMatch = expr.match(/getDateprocessing\(([^)]+)\)/)
            if (funcMatch) {
              const param = funcMatch[1]
              const paramValue = param.startsWith("'") && param.endsWith("'")
                ? param.slice(1, -1)
                : parseInt(param)
              return getDateprocessing(paramValue)
            }
          }
          return match
        } catch (e) {
          console.warn('[TemplateFormatter] 表达式解析失败:', expr, e)
          return match
        }
      })
    } catch (error) {
      console.warn('[TemplateFormatter] 模板格式化失败:', item.key, value, error)
      return null
    }
  }
}

/**
 * 选择器格式化器 - 预留用于未来扩展
 */
class SelectFormatter {
  constructor() {
    this.type = 'select'
  }

  format(item, value) {
    // 预留实现
    return null
  }
}

/**
 * 格式化器配置解析器
 */
class FormatterConfigParser {
  /**
   * 解析格式化器配置
   * @param {Object} item - 配置项
   * @returns {Object|null} 解析后的配置
   */
  static parse(item) {
    if (!item) {
      return null
    }

    // 新格式：formatter对象
    if (item.formatter && typeof item.formatter === 'object') {
      return item.formatter
    }

    // 新格式：formatter字符串 "date:getDateprocessing:month_first"
    if (item.formatter && typeof item.formatter === 'string') {
      return this.parseStringFormatter(item.formatter)
    }

    // 传统格式：从value中解析
    if (item.value && typeof item.value === 'string') {
      return this.parseValueString(item.value)
    }

    return null
  }

  /**
   * 解析字符串格式的formatter
   */
  static parseStringFormatter(formatterString) {
    const parts = formatterString.split(':')
    if (parts.length >= 3) {
      return {
        type: parts[0] + '_processor',
        method: parts[1],
        params: parts.slice(2)
      }
    }
    return null
  }

  /**
   * 从value字符串解析格式化器配置
   */
  static parseValueString(value) {
    if (value.includes('getDateprocessing(')) {
      const match = value.match(/getDateprocessing\(([^)]+)\)/)
      if (match) {
        const param = match[1]
        const paramValue = param.startsWith("'") && param.endsWith("'")
          ? param.slice(1, -1)
          : param
        return {
          type: 'date_processor',
          method: 'getDateprocessing',
          params: [paramValue]
        }
      }
    }
    return null
  }
}

/**
 * 主格式化器工厂类
 */
export default class SearchConfigFormatter {
  constructor() {
    // 注册内置格式化器
    this.formatters = new Map()
    this.formatters.set('date', new DateFormatter())
    this.formatters.set('template', new TemplateFormatter())
    this.formatters.set('select', new SelectFormatter())

    // 性能优化：缓存解析结果
    this.parseCache = new Map()
    this.maxCacheSize = 100
  }

  /**
   * 注册自定义格式化器
   * @param {string} type - 格式化器类型
   * @param {Object} formatter - 格式化器实例
   */
  register(type, formatter) {
    if (type && formatter && typeof formatter.format === 'function') {
      this.formatters.set(type, formatter)
    }
  }

  /**
   * 格式化单个配置项
   * @param {Object} item - 配置项
   * @returns {string|null} 格式化后的值
   */
  formatItem(item) {
    if (!item || !item.key) {
      return null
    }

    try {
      // 尝试新格式化器配置
      const formatterConfig = FormatterConfigParser.parse(item)
      if (formatterConfig) {
        const result = this.formatByConfig(item, formatterConfig)
        if (result !== null) {
          return result
        }
      }

      // 回退到基于类型的格式化
      const formatter = this.formatters.get(item.type)
      if (formatter) {
        return formatter.format(item, item.value)
      }

      return null
    } catch (error) {
      console.warn('[SearchConfigFormatter] 格式化失败:', item.key, error)
      return null
    }
  }

  /**
   * 基于配置的格式化
   */
  formatByConfig(item, formatterConfig) {
    if (formatterConfig.type === 'date_processor') {
      const dateFormatter = this.formatters.get('date')
      return dateFormatter.formatByConfig(formatterConfig)
    }
    return null
  }

  /**
   * 批量格式化配置项
   * @param {Array} items - 配置项数组
   * @returns {Object} 格式化结果对象 {key: value}
   */
  formatBatch(items) {
    if (!Array.isArray(items)) {
      return {}
    }

    const result = {}

    items.forEach(function(item) {
      // ES2017兼容：使用function替代箭头函数
      if (item && item.type === 'date' && item.value && item.key) {
        const formatted = this.formatItem(item)
        if (formatted !== null) {
          result[item.key] = formatted
        }
      }
    }.bind(this))

    return result
  }

  /**
   * 检查是否需要格式化
   * @param {Object} item - 配置项
   * @returns {boolean}
   */
  needsFormatting(item) {
    if (!item) {
      return false
    }

    // 检查新格式配置
    if (item.formatter) {
      return true
    }

    // 检查传统格式
    if (item.type === 'date' && item.value && typeof item.value === 'string') {
      return item.value.includes('getDateprocessing(') || item.value.includes('${')
    }

    return false
  }

  /**
   * 获取支持的格式化器类型
   * @returns {Array} 格式化器类型数组
   */
  getSupportedTypes() {
    return Array.from(this.formatters.keys())
  }

  /**
   * 清理缓存（可选的性能优化）
   */
  clearCache() {
    this.parseCache.clear()
  }
}